import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  alpha,
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  useTheme,
  IconButton,
  Skeleton,
  SpeedDial,
  SpeedDialIcon,
  SpeedDialAction,
  Slide,
  Alert,
} from '@mui/material';
import {
  EditCalendar as EditCalendarIcon,
  Inventory as InventoryIcon,
  Receipt as ReceiptIcon,
  Payment as PaymentIcon,
  SwapHoriz as SwapHorizIcon,
  AccountBalanceWallet as SalaryIcon,
  TrendingUp as ProfitIcon,
  People as PeopleIcon,
  AttachMoney as MoneyIcon,
} from '@mui/icons-material';
import api from '../api';
import StatCard from '../components/StatCard';
import { formatFenWan, effectiveOvertimeWage } from '../utils/money';

// 快捷按钮组件 - 现代化设计（color/bgColor 传 palette 路径，hex 直传仍兼容）
function QuickActionButton({ icon, label, color, bgColor, onClick }) {
  const theme = useTheme();
  const resolve = (path) =>
    path.split('.').reduce((acc, key) => (acc ? acc[key] : undefined), theme.palette) || path;
  const mainColor = resolve(color);
  return (
    <Card
      onClick={onClick}
      sx={{
        cursor: 'pointer',
        backgroundColor: resolve(bgColor),
        borderRadius: 10,
        border: '1px solid transparent',
        '&:hover': {
          borderColor: alpha(mainColor, 0.4),
        },
        transition: 'border-color 0.15s ease',
      }}
    >
      <CardContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1.5,
          p: 3,
          '&:last-child': { pb: 3 },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 48,
            height: 48,
            borderRadius: 8,
            backgroundColor: alpha(mainColor, 0.1),
            color: mainColor,
          }}
        >
          {icon}
        </Box>
        <Typography variant="subtitle1" fontWeight={600} align="center">
          {label}
        </Typography>
      </CardContent>
    </Card>
  );
}



export default function Home() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [cockpit, setCockpit] = useState({ receivableTotal: null, wageDebtTotal: null });
  const [loadError, setLoadError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      // 先算出本月边界：work-logs 接口支持 startDate/endDate 服务端过滤，
      // 按月拉取而不是全表 10 万条再在前端过滤（数据量随历史线性膨胀）
      const now = new Date();
      const year = now.getFullYear();
      const month = now.getMonth() + 1;
      const currentMonth = `${year}-${String(month).padStart(2, '0')}`;
      const lastDay = new Date(year, month, 0).getDate();
      const monthStart = `${currentMonth}-01`;
      const monthEnd = `${currentMonth}-${String(lastDay).padStart(2, '0')}`;

      let failedFetches = 0; // 全部失败→整页告警；部分失败→降级提示（原实现静默渲染全 0）
      const [projectsRes, workersRes, logsRes, incomeRes, materialsRes, expensesRes, recvRes, wageBalRes] =
        await Promise.all([
          // projects 不带分页参数默认只返回第 1 页 50 条，统计会漏算，显式取全量
          api.get('/api/projects?pageSize=100000').catch(() => { failedFetches++; return { data: [] }; }),
          api.get('/api/workers').catch(() => { failedFetches++; return { data: [] }; }),
          // 只拉本月记工（双保险：服务端过滤 + 客户端前缀校验）
          api.get(`/api/work-logs?startDate=${monthStart}&endDate=${monthEnd}&pageSize=100000`).catch(() => { failedFetches++; return { data: [] }; }),
          api.get('/api/incomes').catch(() => { failedFetches++; return { data: [] }; }),
          api.get('/api/materials').catch(() => { failedFetches++; return { data: [] }; }),
          api.get('/api/expenses').catch(() => { failedFetches++; return { data: [] }; }),
          // v2.2 驾驶舱：应收/欠付（失败降级为不显示，不影响月度卡片）
          api.get('/api/reports/receivables').catch(() => ({ data: null })),
          api.get('/api/reports/wage-balance').catch(() => ({ data: null })),
        ]);

      // v2.2 驾驶舱数据（null=接口失败，卡片隐藏）
      setCockpit({
        receivableTotal: recvRes.data ? recvRes.data.totals.receivable : null,
        wageDebtTotal: wageBalRes.data ? wageBalRes.data.totals.balance : null,
      });

      const projects = (projectsRes.data && projectsRes.data.data) || projectsRes.data || [];
      const workers = workersRes.data || [];
      const workLogs = (logsRes.data && logsRes.data.data) || logsRes.data || [];
      const incomes = incomeRes.data || [];
      const materials = materialsRes.data || [];
      const expenses = expensesRes.data || [];

      // 计算本月数据
      const monthlyWorkLogs = workLogs.filter((log) => log.date?.startsWith(currentMonth));
      const monthlyIncomes = incomes.filter((inc) => inc.date?.startsWith(currentMonth));
      const monthlyMaterials = materials.filter((mat) => mat.date?.startsWith(currentMonth));
      const monthlyExpenses = expenses.filter((exp) => exp.date?.startsWith(currentMonth));

      // 计算金额
      const monthlyLaborCost = monthlyWorkLogs.reduce((sum, log) => {
        const dayAmount = ((log.dailyWage || 0) * (log.dayUnits || 0)) / 100;
        // 加班时薪：记工锁定值优先，回退工人当前值（与工资/利润报表口径一致，实现共享于 utils/money）
        const overtimeHourlyWage = effectiveOvertimeWage(log);
        const overtimeAmount = (overtimeHourlyWage * (log.overtimeHoursTimes100 || 0)) / 100;
        return sum + dayAmount + overtimeAmount + (log.mealAllowance || 0);
      }, 0);

      const monthlyMaterialCost = monthlyMaterials.reduce(
        (sum, mat) => sum + (mat.totalAmount || 0),
        0
      );
      const monthlyExpenseCost = monthlyExpenses.reduce(
        (sum, exp) => sum + (exp.amount || 0),
        0
      );
      const monthlyIncome = monthlyIncomes.reduce((sum, inc) => sum + (inc.amount || 0), 0);

      // 计算进行中项目数
      const inProgressProjects = projects.filter((p) => p.status === 1).length;

      setStats({
        projectCount: projects.length,
        workerCount: workers.length,
        inProgressProjects,
        monthlyIncome,
        monthlyLaborCost,
        monthlyMaterialCost,
        monthlyExpenseCost,
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const quickActions = [
    {
      title: '记工',
      icon: <EditCalendarIcon />,
      path: '/work-logs',
      color: 'primary.main',
      bgColor: 'primary.light',
    },
    {
      title: '录材料',
      icon: <InventoryIcon />,
      path: '/materials',
      color: 'success.main',
      bgColor: 'success.light',
    },
    {
      title: '录杂费',
      icon: <ReceiptIcon />,
      path: '/expenses',
      color: 'warning.main',
      bgColor: 'warning.light',
    },
    {
      title: '收录款',
      icon: <PaymentIcon />,
      path: '/incomes',
      color: 'category.violet.main',
      bgColor: 'category.violet.light',
    },
    {
      title: '录借支',
      icon: <SwapHorizIcon />,
      path: '/adjustments',
      color: 'category.slate.main',
      bgColor: 'category.slate.light',
    },
    {
      title: '看工资',
      icon: <SalaryIcon />,
      path: '/salary',
      color: 'category.cyan.main',
      bgColor: 'category.cyan.light',
    },
    {
      title: '看利润',
      icon: <ProfitIcon />,
      path: '/profit',
      color: 'category.emerald.main',
      bgColor: 'category.emerald.light',
    },

  ];

  // 根据时间获取问候语
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 6) return '夜深了';
    if (hour < 9) return '早上好';
    if (hour < 12) return '上午好';
    if (hour < 14) return '中午好';
    if (hour < 18) return '下午好';
    if (hour < 22) return '晚上好';
    return '夜深了';
  };

  // 获取用户名
  const getUserName = () => {
    try {
      const user = JSON.parse(localStorage.getItem('user'));
      return user?.name || user?.username || '用户';
    } catch {
      return '用户';
    }
  };

  if (loading) {
    return (
      <Box>
        <Skeleton variant="rectangular" height={120} sx={{ mb: 3 }} />
        <Skeleton variant="rectangular" height={200} sx={{ mb: 3 }} />
        <Skeleton variant="rectangular" height={100} />
      </Box>
    );
  }

  return (
    <Box>
      {loadError && (
        <Alert severity={loadError.includes('全部') ? 'error' : 'warning'} sx={{ mb: 2 }}>
          {loadError}
        </Alert>
      )}
      {/* 欢迎语区域 */}
      <Box
        sx={{
          backgroundColor: 'primary.light',
          borderRadius: 10,
          p: 4,
          mb: 3,
          border: '1px solid',
          borderColor: 'primary.light',
          borderLeft: 4,
          borderLeftColor: 'primary.main',
        }}
      >
        <Typography variant="h4" fontWeight={700} sx={{ mb: 0.5 }}>
          {getGreeting()}，{getUserName()} 👋
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {stats?.inProgressProjects || 0} 个项目进行中 · 点击下方快捷操作开始记账
        </Typography>
      </Box>

      {/* 快捷录入 */}
      <Typography variant="h6" fontWeight={700} gutterBottom sx={{ mb: 2 }}>
        ⚡ 快捷操作
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {quickActions.map((action) => (
          <Grid item xs={6} sm={3} key={action.title}>
            <QuickActionButton
              icon={action.icon}
              label={action.title}
              color={action.color}
              bgColor={action.bgColor}
              onClick={() => navigate(action.path)}
            />
          </Grid>
        ))}
      </Grid>

      {/* 本月收支 - 现代化统计卡片 */}
      <Typography variant="h5" fontWeight={700} gutterBottom sx={{ mb: 2 }}>
        📊 本月收支
      </Typography>
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <StatCard
            label="已收款"
            value={formatFenWan(stats?.monthlyIncome)}
            color="primary"
            icon={<MoneyIcon />}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard
            label="人工成本"
            value={formatFenWan(stats?.monthlyLaborCost)}
            color="warning"
            icon={<PeopleIcon />}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard
            label="材料成本"
            value={formatFenWan(stats?.monthlyMaterialCost)}
            color="secondary"
            icon={<InventoryIcon />}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard
            label="杂费成本"
            value={formatFenWan(stats?.monthlyExpenseCost)}
            color="error"
            icon={<ReceiptIcon />}
          />
        </Grid>
      </Grid>

      {/* v2.2 经营驾驶舱：钱的两个水位 */}
      {(cockpit.receivableTotal != null || cockpit.wageDebtTotal != null) && (
        <Grid container spacing={2.5} sx={{ mb: 3 }}>
          {cockpit.receivableTotal != null && (
            <Grid item xs={6} sm={3}>
              <StatCard
                label="甲方欠我（应收）"
                value={formatFenWan(cockpit.receivableTotal)}
                color="primary"
                icon={<MoneyIcon />}
              />
            </Grid>
          )}
          {cockpit.wageDebtTotal != null && (
            <Grid item xs={6} sm={3}>
              <StatCard
                label="我欠工人（工资）"
                value={formatFenWan(cockpit.wageDebtTotal)}
                color={cockpit.wageDebtTotal > 0 ? 'error' : 'secondary'}
                icon={<PeopleIcon />}
              />
            </Grid>
          )}
        </Grid>
      )}

      {/* 项目概览 - 现代化大卡片 */}
      <Slide direction="up" in={true}>
        <Card
          sx={{
            cursor: 'pointer',
            borderRadius: 10,
            border: '1px solid',
            borderColor: 'divider',
            borderLeft: 4,
            borderLeftColor: 'secondary.main',
            '&:hover': {
              borderColor: 'secondary.main',
            },
            transition: 'border-color 0.15s ease',
          }}
          onClick={() => navigate('/profit')}
        >
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="h5" fontWeight={700}>
                  📈 项目概览
                </Typography>
                <Typography variant="h2" fontWeight={700} sx={{ mt: 1.5, letterSpacing: '-0.02em', fontVariantNumeric: 'tabular-nums' }}>
                  {stats?.inProgressProjects || 0} 个
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
                  进行中项目
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="body1" color="primary" sx={{ fontWeight: 600 }}>
                  查看利润详情 →
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Slide>

      {/* 快速录入（SpeedDial）：记工/收款/材料/杂费，拇指易达。原 FAB 指向 DataInput 会污染杂费表，已移除该入口 */}
      <SpeedDial
        ariaLabel="快速录入"
        sx={{ position: 'fixed', bottom: 24, right: 24 }}
        icon={<SpeedDialIcon />}
      >
        {[
          { icon: <EditCalendarIcon />, name: '记工', path: '/work-logs' },
          { icon: <PaymentIcon />, name: '收款', path: '/incomes' },
          { icon: <InventoryIcon />, name: '材料', path: '/materials' },
          { icon: <ReceiptIcon />, name: '杂费', path: '/expenses' },
        ].map((action) => (
          <SpeedDialAction
            key={action.name}
            icon={action.icon}
            tooltipTitle={action.name}
            tooltipOpen
            onClick={() => navigate(action.path)}
          />
        ))}
      </SpeedDial>
    </Box>
  );
}
